import React from 'react';
import { Plus, Save, Loader2, Play, RefreshCw, Sparkles, FlaskConical } from 'lucide-react';
import type { EcadAutomacaoPasso, Subtopico } from './types';
import { EcadPassoCard } from './EcadPassoCard';

interface EcadPassosSectionProps {
  selectedSubtopicoId: string | null;
  passos: EcadAutomacaoPasso[];
  passosEditMode: 'visual' | 'json';
  setPassosEditMode: (val: 'visual' | 'json') => void;
  jsonValue: string;
  setJsonValue: (val: string) => void;
  savingPassos: boolean;
  handleAddPasso: () => void;
  handleInsertPasso: (index: number) => void;
  handleRemovePasso: (index: number) => void;
  handleMovePasso: (index: number, direction: 'up' | 'down') => void;
  handleUpdatePassoField: (index: number, field: keyof EcadAutomacaoPasso, value: any) => void;
  handleSavePassos: (passosParaSalvar?: EcadAutomacaoPasso[], silent?: boolean) => Promise<void>;
  handleSaveFromJson: () => Promise<void>;
  setShowScannerModal: (show: boolean) => void;
  setZoomImage: (zoom: { stepIndex: number; type: 'real' | 'foco' } | null) => void;
  onRunFromStep: (ordem: number) => void;
  selectedStepIndexes: number[];
  onToggleSelectStep: (index: number) => void;
  onToggleSelectAllSteps: () => void;
  onActiveEditingIndex: (index: number | null) => void;
  selectedRoteiro: Subtopico | null;
  activeVersion?: { version: string; timestamp: string; actionDescription: string } | null;
  onRunSimulation: () => void;
  isSimulating: boolean;
  handleRegeneratePassos: () => Promise<void>;
  onGeneratePassosViaAi?: () => void;
  uiMap: Record<string, Record<string, { seletor: string; descricao: string }>>;
  // Dry-Run
  onDryRun?: () => void;
  isDryRunning?: boolean;
}

export const EcadPassosSection: React.FC<EcadPassosSectionProps> = ({
  selectedSubtopicoId,
  passos = [],
  passosEditMode,
  setPassosEditMode,
  jsonValue,
  setJsonValue,
  savingPassos,
  handleAddPasso,
  handleInsertPasso,
  handleRemovePasso,
  handleMovePasso,
  handleUpdatePassoField,
  handleSavePassos,
  handleSaveFromJson,
  setShowScannerModal,
  setZoomImage,
  onRunFromStep,
  selectedStepIndexes = [],
  onToggleSelectStep,
  onToggleSelectAllSteps,
  onActiveEditingIndex,
  selectedRoteiro,
  activeVersion,
  onRunSimulation,
  isSimulating,
  handleRegeneratePassos,
  onGeneratePassosViaAi,
  uiMap,
  onDryRun,
  isDryRunning
}) => {
  // Track visual edit states for individual cards to handle automatic edits
  const [editingIndexes, setEditingIndexes] = React.useState<Record<number, boolean>>({});
  const [newSteps, setNewSteps] = React.useState<Record<number, boolean>>({});

  // Detect code modifications comparing current passos array to database copy in selectedRoteiro
  const isModified = React.useMemo(() => {
    if (!selectedRoteiro || !selectedRoteiro.passos) return false;
    if (passos.length !== selectedRoteiro.passos.length) return true;
    for (let i = 0; i < passos.length; i++) {
      const p1 = passos[i];
      const p2 = selectedRoteiro.passos[i];
      if (!p2) return true;
      if (p1.tipoAcao !== p2.tipoAcao) return true;
      if (p1.selectorCss !== p2.selectorCss) return true;
      if ((p1.valor || '') !== (p2.valor || '')) return true;
      if ((p1.legenda || '') !== (p2.legenda || '')) return true;
      if ((p1.urlAtual || '') !== (p2.urlAtual || '')) return true;
      if (!!p1.autenticacaoNecessaria !== !!p2.autenticacaoNecessaria) return true;
    }
    return false;
  }, [passos, selectedRoteiro]);

  // Format date of the active version
  let formattedActiveDate = '';
  if (activeVersion && activeVersion.timestamp) {
    try {
      const datePart = activeVersion.timestamp.split('T')[0];
      const timePart = activeVersion.timestamp.split('T')[1]?.split('-')[0]?.replace(/-/g, ':') || '';
      const [year, month, day] = datePart.split('-');
      formattedActiveDate = `${day}/${month}/${year} ${timePart.split('.')[0]}`;
    } catch (e) {
      formattedActiveDate = activeVersion.timestamp;
    }
  }

  // Monitor passos length to automatically open new step in edit mode
  const prevPassosLengthRef = React.useRef(passos.length);
  React.useEffect(() => {
    if (passos.length > prevPassosLengthRef.current) {
      const newIndex = passos.length - 1;
      setEditingIndexes(prev => ({ ...prev, [newIndex]: true }));
      setNewSteps(prev => ({ ...prev, [newIndex]: true }));
      onActiveEditingIndex(newIndex);
    }
    prevPassosLengthRef.current = passos.length;
  }, [passos.length, onActiveEditingIndex]);

  // Monitor steps to automatically open inserted steps with isNewStepTemp flag
  React.useEffect(() => {
    const newIdx = passos.findIndex(p => (p as any).isNewStepTemp);
    if (newIdx !== -1) {
      setEditingIndexes(prev => ({ ...prev, [newIdx]: true }));
      setNewSteps(prev => ({ ...prev, [newIdx]: true }));
      onActiveEditingIndex(newIdx);
      // Clean up the flag on that step
      handleUpdatePassoField(newIdx, 'isNewStepTemp' as any, undefined);
    }
  }, [passos, onActiveEditingIndex, handleUpdatePassoField]);

  return (
    <section style={{
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
      borderRadius: '12px',
      padding: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#7c3aed', fontFamily: "'Outfit', sans-serif", margin: 0 }}>
            Passos de Automação (Playwright)
          </h3>
          <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '4px 0 0 0', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span>Configure e gerencie as ações sequenciais.</span>
            {activeVersion && (
              <>
                <span style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
                <span style={{ color: '#7c3aed', fontWeight: 600 }}>
                  Versão Usada: {activeVersion.version} ({formattedActiveDate})
                </span>
              </>
            )}
            {isModified && (
              <>
                <span style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
                <span style={{ color: '#f59e0b', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                  ⚠️ Versão modificada (não publicada)
                </span>
              </>
            )}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Alternador de Modo Visual vs JSON */}
          <div style={{
            display: 'flex',
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '3px'
          }}>
            <button
              onClick={() => setPassosEditMode('visual')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: passosEditMode === 'visual' ? '#7c3aed' : 'transparent',
                color: passosEditMode === 'visual' ? '#ffffff' : '#64748b',
                transition: 'all 0.15s'
              }}
            >
              Visual
            </button>
            <button
              onClick={() => setPassosEditMode('json')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: passosEditMode === 'json' ? '#7c3aed' : 'transparent',
                color: passosEditMode === 'json' ? '#ffffff' : '#64748b',
                transition: 'all 0.15s'
              }}
            >
              JSON
            </button>
          </div>

          {passosEditMode === 'visual' && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleAddPasso}
                style={{
                  background: '#ffffff',
                  border: '1px solid #7c3aed',
                  borderRadius: '8px',
                  padding: '6px 14px',
                  fontSize: '0.78rem',
                  color: '#7c3aed',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f3ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                }}
              >
                <Plus size={14} />
                Adicionar Passo
              </button>

              <button
                onClick={onRunSimulation}
                disabled={isSimulating || passos.length === 0}
                style={{
                  background: isSimulating ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px 14px',
                  fontSize: '0.78rem',
                  color: isSimulating ? '#64748b' : '#ffffff',
                  fontWeight: 700,
                  cursor: isSimulating ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: isSimulating ? 'none' : '0 4px 12px rgba(124, 58, 237, 0.25)',
                  transition: 'all 0.2s'
                }}
              >
                {isSimulating ? <Loader2 size={14} className="spin" /> : <Play size={14} fill="currentColor" />}
                {isSimulating ? 'Simulando...' : 'Simular'}
              </button>

              {onDryRun && (
                <button
                  onClick={onDryRun}
                  disabled={isDryRunning || isSimulating || passos.length === 0}
                  style={{
                    background: isDryRunning ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #34d399 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 14px',
                    fontSize: '0.78rem',
                    color: isDryRunning ? '#64748b' : '#ffffff',
                    fontWeight: 700,
                    cursor: isDryRunning ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: isDryRunning ? 'none' : '0 4px 12px rgba(5, 150, 105, 0.25)',
                    transition: 'all 0.2s'
                  }}
                >
                  {isDryRunning ? <Loader2 size={14} className="spin" /> : <FlaskConical size={14} />}
                  {isDryRunning ? 'Testando...' : 'Testar'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modo Visual dos Passos */}
      {passosEditMode === 'visual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {passos.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 4px', marginBottom: '4px' }}>
              <input 
                type="checkbox" 
                checked={passos.length > 0 && selectedStepIndexes.length === passos.length}
                onChange={onToggleSelectAllSteps}
                style={{
                  width: '15px',
                  height: '15px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  cursor: 'pointer',
                  accentColor: '#7c3aed'
                }}
              />
              <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                {selectedStepIndexes.length === passos.length ? 'Desmarcar todos' : `Selecionar todos (${passos.length})`}
              </span>
              {selectedStepIndexes.length > 0 && (
                <span style={{ fontSize: '0.75rem', color: '#7c3aed', marginLeft: 'auto', fontWeight: 700 }}>
                  {selectedStepIndexes.length} selecionado(s)
                </span>
              )}
            </div>
          )}

          {passos.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '30px 20px',
              background: '#f8fafc',
              border: '1px dashed #cbd5e1',
              borderRadius: '12px',
              color: '#64748b',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px'
            }}>
              <span style={{ fontSize: '0.85rem' }}>
                Nenhum passo de automação cadastrado. Clique em "Adicionar Passo" ou utilize o scanner.
              </span>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {onGeneratePassosViaAi && (
                  <button
                    onClick={onGeneratePassosViaAi}
                    style={{
                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 18px',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.25)',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      outline: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.25)';
                    }}
                  >
                    <Sparkles size={14} style={{ color: '#ffffff' }} />
                    Gerar Roteiro pela IA
                  </button>
                )}

                <button
                  onClick={handleAddPasso}
                  style={{
                    background: '#ffffff',
                    border: '2px solid #7c3aed',
                    color: '#7c3aed',
                    borderRadius: '8px',
                    padding: '10px 18px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.15)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    outline: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.background = '#f5f3ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  <Plus size={14} />
                  Adicionar Passo Manual
                </button>
              </div>
            </div>
          ) : (
            passos.map((passo, index) => {
              return (
                <React.Fragment key={index}>
                  {/* Floating Insert-Before circular button */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '0px',
                    position: 'relative',
                    zIndex: 10
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInsertPasso(index);
                      }}
                      style={{
                        position: 'absolute',
                        transform: 'translateY(-50%)',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 0 8px rgba(139, 92, 246, 0.25)',
                        transition: 'all 0.2s',
                        opacity: 0.35,
                        outline: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.2)';
                        e.currentTarget.style.background = '#7c3aed';
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.borderColor = '#7c3aed';
                        e.currentTarget.style.boxShadow = '0 0 12px rgba(139, 92, 246, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.35';
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.color = '#64748b';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.boxShadow = '0 0 8px rgba(139, 92, 246, 0.25)';
                      }}
                      title="Inserir passo antes"
                    >
                      <Plus size={12} />
                    </button>
                  </div>

                  <EcadPassoCard
                    passo={passo}
                    index={index}
                    selectedSubtopicoId={selectedSubtopicoId}
                    selectedStepIndexes={selectedStepIndexes}
                    onToggleSelectStep={onToggleSelectStep}
                    handleUpdatePassoField={handleUpdatePassoField}
                    handleRemovePasso={handleRemovePasso}
                    handleMovePasso={handleMovePasso}
                    handleSavePassos={handleSavePassos}
                    onRunFromStep={onRunFromStep}
                    setZoomImage={setZoomImage}
                    onActiveEditingIndex={onActiveEditingIndex}
                    isEditing={!!editingIndexes[index]}
                    setIsEditing={(editing) => setEditingIndexes(prev => ({ ...prev, [index]: editing }))}
                    isNewStep={!!newSteps[index]}
                    setIsNewStep={(newStep) => setNewSteps(prev => ({ ...prev, [index]: newStep }))}
                    passosLength={passos.length}
                    savingPassos={savingPassos}
                    uiMap={uiMap}
                  />
                </React.Fragment>
              );
            })
          )}

          {/* final circular plus button */}
          {passos.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '32px',
              position: 'relative',
              marginTop: '12px'
            }}>
              <button
                onClick={() => handleInsertPasso(passos.length)}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 0 10px rgba(139, 92, 246, 0.2)',
                  transition: 'all 0.2s',
                  opacity: 0.5,
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'scale(1.15)';
                  e.currentTarget.style.background = '#7c3aed';
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.borderColor = '#7c3aed';
                  e.currentTarget.style.boxShadow = '0 0 15px rgba(139, 92, 246, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.5';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.color = '#64748b';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.boxShadow = '0 0 10px rgba(139, 92, 246, 0.2)';
                }}
                title="Adicionar passo ao final"
              >
                <Plus size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modo JSON */}
      {passosEditMode === 'json' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
              Esquema do Roteiro (.json formatado)
            </span>
            <span style={{ fontSize: '0.7rem', color: '#10b981', fontFamily: 'monospace' }}>
              Status: Editável
            </span>
          </div>

          <textarea
            value={jsonValue}
            onChange={(e) => setJsonValue(e.target.value)}
            rows={18}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#000000',
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: '0.82rem',
              lineHeight: '1.5',
              resize: 'vertical',
              boxSizing: 'border-box',
              outline: 'none',
              fontWeight: 500
            }}
            placeholder="[ { 'tipoAcao': 'CLICK', ... } ]"
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              onClick={handleRegeneratePassos}
              disabled={savingPassos}
              style={{
                background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '0.82rem',
                color: '#ffffff',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 15px rgba(124, 58, 237, 0.2)'
              }}
            >
              {savingPassos ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
              Regenerar JSON
            </button>

            <button
              onClick={handleSaveFromJson}
              disabled={savingPassos}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '0.82rem',
                color: '#ffffff',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)'
              }}
            >
              {savingPassos ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
              Salvar JSON no Banco/Disco
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
